import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    Box,
    Typography,
    Paper,
    Chip,
    Button,
    IconButton,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
    FormControlLabel,
    Checkbox,
    Alert,
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    Delete as DeleteIcon,
    Person as PersonIcon,
    LocalShipping as VozidloIcon,
    Home as StaniceIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/admin';
import InventarFotky from '../../components/inventar/InventarFotky';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/cs';
import {
    useInventarPolozkaByIdQuery,
    useInventarHistorieQuery,
    useInventarPrirazeniQuery,
    useVydejInventarPolozkuMutation,
    useVratInventarPolozkuMutation,
    useUpdateInventarPolozkaMutation,
    useKontrolyForPolozkaQuery,
    useInventarKontrolyHistorieQuery,
    useCreatePolozkaKontrolaMutation,
    useDeletePolozkaKontrolaMutation,
} from '../../api/JsdhNotifikaceService/service/InventarMutations';
import AdminServiceApi from '../../api/JsdhNotifikaceService/service/AdminService';
import type {
    VydejInventarPolozkyRequest,
    VraceniInventarPolozkyRequest,
    InventarKontrolaStatusResponse,
    CreateInventarKontrolaRequest,
} from '../../api/JsdhNotifikaceService/generated/models/Inventar';
import { axiosInstance } from '../../api/JsdhNotifikaceService/service/JsdhNotifikaceService';
import { ADMIN_INVENTAR_QR_URL } from '../../api/JsdhNotifikaceService/url';

dayjs.locale('cs');

const formatDate = (d?: string) => d ? dayjs(d).format('DD.MM.YYYY') : '—';

// ==========================================
// HELPER: Status Chip pro revize OOP
// ==========================================

const RevizeStatusChip: React.FC<{ status: InventarKontrolaStatusResponse }> = ({ status }) => {
    switch (status.status) {
        case 'PLATNA':
            return (
                <Chip
                    color="success"
                    label={`Platná do ${dayjs(status.datumExpirace).format('DD.MM.YYYY')}`}
                    size="small"
                />
            );
        case 'BLIZI_SE_EXPIRACE':
            return (
                <Chip
                    color="warning"
                    label={`Vyprší ${dayjs(status.datumExpirace).format('DD.MM.YYYY')}`}
                    size="small"
                />
            );
        case 'EXPIROVANA':
            return (
                <Chip
                    color="error"
                    label={`Expirovaná ${dayjs(status.datumExpirace).format('DD.MM.YYYY')}`}
                    size="small"
                />
            );
        case 'CHYBI':
            return <Chip variant="outlined" label="Chybí" size="small" />;
        default:
            return null;
    }
};

// ==========================================
// DIALOG: Zaznamenat / Historie revize OOP (immutable)
// ==========================================

const InventarRevizeDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    polozkaId: number;
    polozkaNazev: string;
    revizeStatus: InventarKontrolaStatusResponse;
}> = ({ open, onClose, polozkaId, polozkaNazev, revizeStatus }) => {
    const [datumSplneni, setDatumSplneni] = useState<Dayjs | null>(dayjs());
    const [poznamka, setPoznamka] = useState('');

    const createMutation = useCreatePolozkaKontrolaMutation();
    const deleteMutation = useDeletePolozkaKontrolaMutation();
    const { data: historie } = useInventarKontrolyHistorieQuery(
        open ? polozkaId : null,
        open ? revizeStatus.typKontrolyId : null
    );

    const handleReset = () => {
        setDatumSplneni(dayjs());
        setPoznamka('');
    };

    const handleClose = () => {
        handleReset();
        onClose();
    };

    const handleSave = () => {
        if (!datumSplneni) return;
        const request: CreateInventarKontrolaRequest = {
            typKontrolyId: revizeStatus.typKontrolyId,
            datumSplneni: datumSplneni.format('YYYY-MM-DD'),
            poznamka: poznamka || undefined,
        };
        createMutation.mutate({ polozkaId, data: request }, {
            onSuccess: () => handleReset(),
        });
    };

    const handleDelete = (kontrolaId: number) => {
        if (window.confirm('Opravdu smazat tento záznam? Revize jsou neměnné — pro opravu smazejte a vytvořte nový záznam.')) {
            deleteMutation.mutate({ polozkaId, kontrolaId });
        }
    };

    const isSaving = createMutation.isPending;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {revizeStatus.typKontrolyNazev} — {polozkaNazev}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 1 }}>
                    <DatePicker
                        label="Datum splnění"
                        value={datumSplneni}
                        onChange={setDatumSplneni}
                        format="DD.MM.YYYY"
                        slotProps={{ textField: { fullWidth: true, size: 'small', margin: 'dense' } }}
                    />
                    <TextField
                        label="Poznámka"
                        value={poznamka}
                        onChange={(e) => setPoznamka(e.target.value)}
                        fullWidth
                        margin="dense"
                        size="small"
                        multiline
                        rows={2}
                    />
                </Box>
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={!datumSplneni || isSaving}
                        size="small"
                    >
                        {isSaving ? <CircularProgress size={20} /> : 'Zaznamenat'}
                    </Button>
                </Box>

                {historie && historie.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>Historie revizí</Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Datum splnění</TableCell>
                                        <TableCell>Expirace</TableCell>
                                        <TableCell>Poznámka</TableCell>
                                        <TableCell align="right">Akce</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {historie.map((record) => (
                                        <TableRow key={record.id}>
                                            <TableCell>{dayjs(record.datumSplneni).format('DD.MM.YYYY')}</TableCell>
                                            <TableCell>{dayjs(record.datumExpirace).format('DD.MM.YYYY')}</TableCell>
                                            <TableCell>{record.poznamka || '—'}</TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Smazat">
                                                    <IconButton size="small" onClick={() => handleDelete(record.id)}>
                                                        <DeleteIcon fontSize="small" color="error" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}
                {historie && historie.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Žádné záznamy revizí.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Zavřít</Button>
            </DialogActions>
        </Dialog>
    );
};

const InventarDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const polozkaId = parseInt(id ?? '0');

    const [tab, setTab] = useState(0);
    const [vydejOpen, setVydejOpen] = useState(false);
    const [vraceniOpen, setVraceniOpen] = useState(false);
    const [revizeDialogState, setRevizeDialogState] = useState<InventarKontrolaStatusResponse | null>(null);

    // Výdej form
    const [vydejUzivatelId, setVydejUzivatelId] = useState<number | null>(null);
    const [vydejDatum, setVydejDatum] = useState(dayjs().format('YYYY-MM-DD'));
    const [vydejStav, setVydejStav] = useState('NOVA');
    const [vydejNotifikace, setVydejNotifikace] = useState(false);
    const [vydejPoznamka, setVydejPoznamka] = useState('');

    // Vrácení form
    const [vraceniDatum, setVraceniDatum] = useState(dayjs().format('YYYY-MM-DD'));
    const [vraceniStav, setVraceniStav] = useState('OK');
    const [vraceniPoznamka, setVraceniPoznamka] = useState('');

    const { data: polozka, isLoading } = useInventarPolozkaByIdQuery(polozkaId);
    const { data: historie } = useInventarHistorieQuery(tab === 0 ? polozkaId : null);
    const { data: prirazeni } = useInventarPrirazeniQuery(tab === 1 ? polozkaId : null);
    const { data: revizeStatus, isLoading: revizeLoading, isError: revizeError } = useKontrolyForPolozkaQuery(tab === 3 ? polozkaId : null);
    const { data: uzivatele } = useQuery({ queryKey: ['uzivatele'], queryFn: () => AdminServiceApi.getAllUsers(), staleTime: 5 * 60 * 1000 });

    const vydejMutation = useVydejInventarPolozkuMutation();
    const vraceniMutation = useVratInventarPolozkuMutation();

    if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
    if (!polozka) return <Typography>Položka nenalezena</Typography>;

    const jePrivrazena = polozka.prirazeniTyp === 'CLEN';

    const handleVydej = () => {
        if (!vydejUzivatelId) return;
        vydejMutation.mutate({
            polozkaId,
            data: { uzivatelId: vydejUzivatelId, datumVydeje: vydejDatum, stavPriVydeji: vydejStav, poznamka: vydejPoznamka || undefined, odeslatNotifikaci: vydejNotifikace },
        }, {
            onSuccess: () => {
                setVydejOpen(false);
                setVydejUzivatelId(null);
                setVydejStav('NOVA');
                setVydejPoznamka('');
            },
        });
    };

    const handleVraceni = () => {
        vraceniMutation.mutate({
            polozkaId,
            data: { datumVraceni: vraceniDatum, stavPriVraceni: vraceniStav, poznamka: vraceniPoznamka || undefined },
        }, {
            onSuccess: () => {
                setVraceniOpen(false);
                setVraceniPoznamka('');
            },
        });
    };

    return (
        <Box>
            <PageHeader
                title={polozka.nazev}
                icon={<BackIcon sx={{ cursor: 'pointer' }} onClick={() => navigate('/admin/inventar')} />}
                action={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {!jePrivrazena && (
                            <Button variant="contained" startIcon={<PersonIcon />} onClick={() => setVydejOpen(true)}>
                                Vydat členu
                            </Button>
                        )}
                        {jePrivrazena && (
                            <Button variant="outlined" color="warning" onClick={() => setVraceniOpen(true)}>
                                Vrátit
                            </Button>
                        )}
                    </Box>
                }
            />

            <Alert severity="info" sx={{ mb: 2 }}>
                Beta verze — modul Inventář slouží k otestování funkcí.
            </Alert>

            {/* Základní info */}
            <Paper sx={{ p: 3, mb: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    <Box sx={{ flex: 1, minWidth: 250 }}>
                        <Typography variant="body2" color="text.secondary">Kategorie</Typography>
                        <Typography>{polozka.kategorieNazev}</Typography>

                        {polozka.vyrobce && <>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Výrobce / Model</Typography>
                            <Typography>{polozka.vyrobce}{polozka.model && ` ${polozka.model}`}</Typography>
                        </>}

                        {polozka.velikost && <>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Velikost</Typography>
                            <Typography>{polozka.velikost}</Typography>
                        </>}

                        {polozka.serioveCislo && <>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Výrobní číslo (SN)</Typography>
                            <Typography fontFamily="monospace">{polozka.serioveCislo}</Typography>
                        </>}
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 250 }}>
                        <Typography variant="body2" color="text.secondary">Inventární číslo</Typography>
                        {polozka.inventarniCislo ? (
                            <Typography fontFamily="monospace">{polozka.inventarniCislo}</Typography>
                        ) : (
                            <Chip label="Čeká na účetní" color="warning" size="small" variant="outlined" />
                        )}
                        <Typography variant="caption" color="text.secondary" display="block">
                            Evidenční číslo majetku — přiděluje účetní (nebo systém dle nastavení).
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>QR kód</Typography>
                        <Typography fontFamily="monospace" variant="body2">{polozka.qrKod}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Interní kód pro sken a štítky — tiskne se hned po pořízení.
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Stav</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Chip
                                label={polozka.stav === 'AKTIVNI' ? 'Aktivní' : polozka.stav === 'V_OPRAVE' ? 'V opravě' : polozka.stav}
                                color={polozka.stav === 'AKTIVNI' ? 'success' : polozka.stav === 'V_OPRAVE' ? 'warning' : 'error'}
                                size="small"
                            />
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Přiřazení</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {polozka.prirazeniTyp === 'CLEN' && <PersonIcon fontSize="small" />}
                            {polozka.prirazeniTyp === 'VOZIDLO' && <VozidloIcon fontSize="small" />}
                            {polozka.prirazeniTyp === 'STANICE' && <StaniceIcon fontSize="small" />}
                            <Typography>{polozka.prirazeniNazev}</Typography>
                        </Box>
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 250 }}>
                        {polozka.datumNakupu && <>
                            <Typography variant="body2" color="text.secondary">Datum nákupu</Typography>
                            <Typography>{formatDate(polozka.datumNakupu)}</Typography>
                        </>}

                        {polozka.porizovaciCena != null && <>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Pořizovací cena</Typography>
                            <Typography>{polozka.porizovaciCena.toLocaleString('cs-CZ')} Kč</Typography>
                        </>}

                        {polozka.poznamka && <>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Poznámka</Typography>
                            <Typography variant="body2">{polozka.poznamka}</Typography>
                        </>}
                    </Box>
                </Box>
            </Paper>

            {/* Taby */}
            <Paper sx={{ mb: 2 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                    <Tab label="Audit log" />
                    <Tab label="Historie přiřazení" />
                    <Tab label="QR kód" />
                    <Tab label="Revize OOP" />
                    <Tab label="Fotky" />
                </Tabs>
            </Paper>

            {tab === 0 && (
                <Paper sx={{ p: 2 }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Datum</TableCell>
                                    <TableCell>Typ</TableCell>
                                    <TableCell>Popis</TableCell>
                                    <TableCell>Kdo</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {historie?.map(h => (
                                    <TableRow key={h.id}>
                                        <TableCell>{formatDate(h.datum)}</TableCell>
                                        <TableCell><Chip label={h.typZmeny} size="small" variant="outlined" /></TableCell>
                                        <TableCell>{h.popis}</TableCell>
                                        <TableCell>{h.uzivatelJmeno ?? '—'}</TableCell>
                                    </TableRow>
                                ))}
                                {(!historie || !historie.length) && (
                                    <TableRow><TableCell colSpan={4} align="center">Žádné záznamy</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {tab === 1 && (
                <Paper sx={{ p: 2 }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Člen</TableCell>
                                    <TableCell>Vydáno</TableCell>
                                    <TableCell>Vráceno</TableCell>
                                    <TableCell>Stav výdeje</TableCell>
                                    <TableCell>Stav vrácení</TableCell>
                                    <TableCell>Podpis</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {prirazeni?.map(pr => (
                                    <TableRow key={pr.id} sx={{ bgcolor: !pr.datumVraceni ? 'action.selected' : undefined }}>
                                        <TableCell sx={{ fontWeight: !pr.datumVraceni ? 500 : 400 }}>{pr.uzivatelJmeno}</TableCell>
                                        <TableCell>{formatDate(pr.datumVydeje)}</TableCell>
                                        <TableCell>{pr.datumVraceni ? formatDate(pr.datumVraceni) : <Chip label="Aktivní" color="info" size="small" />}</TableCell>
                                        <TableCell>{pr.stavPriVydeji ?? '—'}</TableCell>
                                        <TableCell>{pr.stavPriVraceni ?? '—'}</TableCell>
                                        <TableCell>{pr.podpisVydej ?? '—'}</TableCell>
                                    </TableRow>
                                ))}
                                {(!prirazeni || !prirazeni.length) && (
                                    <TableRow><TableCell colSpan={6} align="center">Žádná historie přiřazení</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {tab === 2 && <QrKodTab polozkaId={polozkaId} qrKod={polozka.qrKod} />}

            {tab === 3 && (
                <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Revize OOP</Typography>
                    {revizeLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : revizeError ? (
                        <Alert severity="error">Chyba při načítání revizí OOP.</Alert>
                    ) : !revizeStatus || revizeStatus.length === 0 ? (
                        <Alert severity="info">
                            Položka nemá nastavený typ revize. Nastavíte ho v úpravě položky (tužka na seznamu inventáře).
                        </Alert>
                    ) : (
                        <>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Typ revize</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Poslední splnění</TableCell>
                                            <TableCell>Expirace</TableCell>
                                            <TableCell align="right">Akce</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {revizeStatus.map((revize) => (
                                            <TableRow key={revize.typKontrolyId}>
                                                <TableCell>{revize.typKontrolyNazev}</TableCell>
                                                <TableCell>
                                                    <RevizeStatusChip status={revize} />
                                                </TableCell>
                                                <TableCell>
                                                    {revize.datumSplneni
                                                        ? dayjs(revize.datumSplneni).format('DD.MM.YYYY')
                                                        : '–'}
                                                </TableCell>
                                                <TableCell>
                                                    {revize.datumExpirace
                                                        ? dayjs(revize.datumExpirace).format('DD.MM.YYYY')
                                                        : '–'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => setRevizeDialogState(revize)}
                                                    >
                                                        Zaznamenat / Historie
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {revizeDialogState && (
                                <InventarRevizeDialog
                                    open={true}
                                    onClose={() => setRevizeDialogState(null)}
                                    polozkaId={polozkaId}
                                    polozkaNazev={polozka.nazev}
                                    revizeStatus={revizeDialogState}
                                />
                            )}
                        </>
                    )}
                </Paper>
            )}

            {tab === 5 && <InventarFotky polozkaId={polozkaId} />}

            {/* Výdej dialog */}
            <Dialog open={vydejOpen} onClose={() => setVydejOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Vydat: {polozka.nazev}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <Autocomplete
                            options={uzivatele ?? []}
                            getOptionLabel={(u: any) => u.jmeno}
                            onChange={(_, u: any) => setVydejUzivatelId(u?.id ?? null)}
                            renderInput={(params) => <TextField {...params} label="Člen" required />}
                        />
                        <TextField
                            label="Datum výdeje"
                            type="date"
                            value={vydejDatum}
                            onChange={(e) => setVydejDatum(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <FormControl required>
                            <InputLabel>Stav při výdeji</InputLabel>
                            <Select value={vydejStav} onChange={(e) => setVydejStav(e.target.value)} label="Stav při výdeji">
                                <MenuItem value="NOVA">Nová</MenuItem>
                                <MenuItem value="V_POUZIVANI">V používání</MenuItem>
                                <MenuItem value="OPOTREBENA">Opotřebená</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Poznámka"
                            multiline
                            rows={2}
                            value={vydejPoznamka}
                            onChange={(e) => setVydejPoznamka(e.target.value)}
                        />
                        <FormControlLabel
                            control={<Checkbox checked={vydejNotifikace} onChange={(e) => setVydejNotifikace(e.target.checked)} />}
                            label="Odeslat notifikaci členu o přidělení"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setVydejOpen(false)}>Zrušit</Button>
                    <Button variant="contained" onClick={handleVydej} disabled={!vydejUzivatelId || vydejMutation.isPending}>
                        Vydat
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Vrácení dialog */}
            <Dialog open={vraceniOpen} onClose={() => setVraceniOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Vrátit: {polozka.nazev}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Datum vrácení"
                            type="date"
                            value={vraceniDatum}
                            onChange={(e) => setVraceniDatum(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <FormControl required>
                            <InputLabel>Stav při vrácení</InputLabel>
                            <Select value={vraceniStav} onChange={(e) => setVraceniStav(e.target.value)} label="Stav při vrácení">
                                <MenuItem value="OK">V pořádku</MenuItem>
                                <MenuItem value="OPOTREBENA">Opotřebená</MenuItem>
                                <MenuItem value="POSKOZENA">Poškozená</MenuItem>
                                <MenuItem value="ZTRACENA">Ztracená</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Poznámka"
                            multiline
                            rows={2}
                            value={vraceniPoznamka}
                            onChange={(e) => setVraceniPoznamka(e.target.value)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setVraceniOpen(false)}>Zrušit</Button>
                    <Button variant="contained" color="warning" onClick={handleVraceni} disabled={vraceniMutation.isPending}>
                        Vrátit
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

// QR kód tab — načte obrázek přes autorizovaný axios (ne přímý <img src>)
const QrKodTab: React.FC<{ polozkaId: number; qrKod: string }> = ({ polozkaId, qrKod }) => {
    // React Query cache drží pouze surový Blob (bezpečně cachovatelné, žádný lifecycle problém).
    // Object URL se odvozuje per-mount níže, aby ho šlo bezpečně revokovat při unmountu
    // bez rizika, že revokneme URL, které cache ještě vrací dalšímu mountu (viz níže).
    const { data: blob } = useQuery<Blob | null>({
        queryKey: ['inventar-qr', polozkaId],
        queryFn: async () => {
            try {
                const res = await axiosInstance.get(ADMIN_INVENTAR_QR_URL(polozkaId, 300), { responseType: 'blob' });
                return res.data;
            } catch {
                return null;
            }
        },
        staleTime: 10 * 60 * 1000,
    });

    // Object URL žije jen po dobu mountu — vytvoří se nad (cachovaným) blobem a revokuje
    // se v cleanupu tohoto effectu, takže se nikdy nedostane do React Query cache.
    const [blobUrl, setBlobUrl] = useState<string | null>(null);

    useEffect(() => {
        if (!blob) {
            setBlobUrl(null);
            return;
        }
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
        return () => {
            URL.revokeObjectURL(url);
        };
    }, [blob]);

    const handleDownload = async () => {
        try {
            const res = await axiosInstance.get(ADMIN_INVENTAR_QR_URL(polozkaId, 600), { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `qr-${qrKod}.png`;
            a.click();
            URL.revokeObjectURL(url);
        } catch { /* ignore */ }
    };

    return (
        <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            {blobUrl ? (
                <Box
                    component="img"
                    src={blobUrl}
                    alt={`QR: ${qrKod}`}
                    sx={{ width: 300, height: 300, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                />
            ) : (
                <Box sx={{ width: 300, height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <CircularProgress />
                </Box>
            )}
            <Typography variant="body2" fontFamily="monospace">{qrKod}</Typography>
            <Button variant="outlined" onClick={handleDownload}>
                Stáhnout PNG
            </Button>
        </Paper>
    );
};

export default InventarDetailPage;
